import {
  fetchUpdatesOfWeekChanges,
  fetchMemoMaterials,
  fetchCalendarMilestones,
  fetchUpdatesOfWeeks,
  fetchCalendars,
  ICommonParams,
} from '@gp/services/dpsReport/v3';
import { formatDateStr, getFirstDayOfMonth, getLastDayOfMonth } from '@gp/utils/common';
import { DataType } from '@gp/enums/common';
import { MilestoneDateStatus } from '@gp/enums/milestone';

import type { IDateRange } from '@home/types/common';
import type { IMaterialDTO, IMilestoneSimpleDTO } from '@gp/types/global';
import type { IDPSReportFilter, IDPSReportCalendarDTO } from '@gp/types/dpsReport/common';
import type {
  IDPSReportSpecialDTOV3,
  IDPSReportUpdatesOfTheWeekDTO,
  IDPSReportUpdatesOfTheWeekChangeDTO,
  IDPSReportMemoPendingApprovalMemoInfoVO,
} from '@gp/types/dpsReport/v3';

export const getUpdatesOfWeekChanges = async (
  params: ICommonParams,
): Promise<IDPSReportUpdatesOfTheWeekChangeDTO[] | null> => {
  let changes: any = null;
  try {
    const res = await fetchUpdatesOfWeekChanges(params);
    const { list } = res?.data;
    changes = !Array.isArray(list) || !list.length ? null : list;
  } catch (exception) {}
  return changes || null;
};

export const getMemoMaterials = async (params: ICommonParams): Promise<IDPSReportMemoPendingApprovalMemoInfoVO> => {
  const result: {
    milestone_id: string;
    material: IMaterialDTO[];
    comment: string;
    date: string;
    date_confirm: MilestoneDateStatus;
  } = {
    milestone_id: '',
    material: [],
    comment: '',
    date: '',
    date_confirm: MilestoneDateStatus.Confirm,
  };
  try {
    const res = await fetchMemoMaterials(params);
    const { milestone_id: milestoneId, material: materials, comment, date, date_confirm: dateConfirm } = res?.data;
    result.milestone_id = milestoneId || '';
    result.material = !Array.isArray(materials) ? [] : materials;
    result.comment = comment || '';
    result.date = date || '';
    result.date_confirm = dateConfirm || MilestoneDateStatus.Confirm;
  } catch (exception) {}
  return result;
};

export const getCalendarMilestones = async (params: {
  project_id: string[];
  start_date: string;
  end_date: string;
}): Promise<IMilestoneSimpleDTO[]> => {
  let milestones: IMilestoneSimpleDTO[] = [];
  try {
    const res = await fetchCalendarMilestones(params);
    const { list } = res?.data;
    milestones = !Array.isArray(list) ? [] : list.map((item) => ({ ...item, date: formatDateStr(item?.date) }));
  } catch (exception) {}
  return milestones;
};

const addAutoFetchedDataType = <T>(list?: T[]): T[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => ({ ...item, data_type: DataType.AutoFetched }));
};

export const getInitialDPSReport = async (
  filter: IDPSReportFilter,
  calendarsDates: IDateRange,
): Promise<Pick<IDPSReportSpecialDTOV3, 'changes' | 'calendar'>> => {
  const { start_date: startDate, end_date: endDate } = filter || {};
  const { start_date: calendarsStartDate, end_date: calendarsEndDate } = calendarsDates || {};
  const changesPromise = fetchUpdatesOfWeeks({
    start_date: startDate,
    end_date: endDate,
  });
  const calendarPromise = fetchCalendars({
    start_date: getFirstDayOfMonth(calendarsStartDate),
    end_date: getLastDayOfMonth(calendarsEndDate),
  });
  const [changesRes, calendarRes] = await Promise.allSettled([changesPromise, calendarPromise]);

  const changes = changesRes?.status !== 'fulfilled' ? [] : changesRes?.value?.data?.list || [];

  const calendar = calendarRes?.status !== 'fulfilled' ? [] : calendarRes?.value?.data?.list || [];

  return Promise.resolve({
    changes: addAutoFetchedDataType<IDPSReportUpdatesOfTheWeekDTO>(changes),
    calendar: {
      start_date: '',
      end_date: '',
      list: addAutoFetchedDataType<IDPSReportCalendarDTO>(calendar),
    },
  });
};
